import { Badge, Box, Button, Flex, HStack, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { RpcError } from "grpc-web";
import { isEmpty } from "lodash";
import { FunctionComponent, ReactElement, useState } from "react";
import { Link, generatePath, useNavigate, useSearchParams } from "react-router-dom";

// Components
import LinkableTableCell from "../../shared/DataTable/shared/LinkableTableCell";
import AttorneyActiveBadge from "../../shared/AttorneyActiveBadge/AttorneyActiveBadge";
import LayoutWrapper from "../../shared/layout";
import ClaimsIndexTable from "./index/ClaimsIndexTable";

// Hooks
import { useAuth } from "../../hooks/useAuth";
import useIndex from "../../hooks/useIndex";
import usePermission from "../../hooks/usePermission";
import useClaimsToast from "../../hooks/useToast";
import { getCacheKeyObject, IParams } from "../../shared/DataTable/hooks/useSearchParams";

// Utils
import { RouterPathMapping } from "../../Router";
import { getClaimsList } from "../../utils/api/claims";
import { ClaimStatus, UserRole, dummyPaginatedApiResponse } from "../../utils/consts";
import {
	convertProtoDateToDateTime,
	convertUnixTimestampToLocalDate,
	getClaimStatusFromBffStatus,
	createExternalLinkFromEnv,
	doesUserHaveRoles
} from "../../utils/helpers";
import {
	CustomColumnFilterType,
	DataTableColumn,
	KVP,
	PaginatedApiResponse,
	PaginationResponse,
	SortDirection
} from "../../utils/types";

//Consts
import { EnvKeys } from "../../utils/consts";

// Protos
import {
	Claim as ApiClaim,
	ClaimPerson,
	ClaimsListResponse,
	ClaimsStatus
} from "../../utils/api/protos/messages/claims.message_pb";
import { PaginationResponse as ApiPaginationResponse } from "../../utils/api/protos/messages/core.message_pb";

interface ClaimsPageProps {}

/**
 * Define tabs on the page
 */
enum ClaimsTabs {
	Assigned,
	All,
	AnalystDiary,
	ManagerDiary,
	AdminDiary
}

/**
 * Handles the Claims listing page
 * Shows different data depending on which tab is active
 * Each individual tab is rendered as a child component
 * @param props typeof: ClaimsPageProps
 * @returns ReactElement
 */
const ClaimsPage: FunctionComponent<ClaimsPageProps> = (props: ClaimsPageProps): ReactElement => {
	// State
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [analystDiaryClaimsData, setAnalystDiaryClaimsData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const [managerDiaryClaimsData, setManagerDiaryClaimsData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const [adminDiaryClaimsData, setAdminDiaryClaimsData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const [allClaimsData, setAllClaimsData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const [assignedClaimsData, setAssignedClaimsData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const [analystDiaryCacheKey, setAnalystDiaryCacheKey] = useState<string>("");
	const [managerDiaryCacheKey, setManagerDiaryCacheKey] = useState<string>("");
	const [adminDiaryCacheKey, setAdminDiaryCacheKey] = useState<string>("");
	const [allCacheKey, setAllCacheKey] = useState<string>("");
	const [assignedCacheKey, setAssignedCacheKey] = useState<string>("");
	const { getUser } = useAuth();
	const { hasGeneralAdminAccess, hasGeneralClaimsAccess } = usePermission();
	const user = getUser();

	// Define default params for different tabs
	const analystDiaryDefaultParams: Partial<IParams> = {
		sortDirection: SortDirection.Asc,
		sortField: "dueDate"
	};

	const managerDiaryDefaultParams: Partial<IParams> = {
		sortDirection: SortDirection.Asc,
		sortField: "dueDate"
	};

	const adminDiaryDefaultParams: Partial<IParams> = {
		sortDirection: SortDirection.Asc,
		sortField: "dueDate"
	};

	const assignedClaimsDefaultParams: Partial<IParams> = {
		sortDirection: SortDirection.Asc,
		sortField: "dueDate"
	};

	const allClaimsDefaultParams: Partial<IParams> = {
		sortDirection: SortDirection.Desc,
		sortField: "createdAt"
	};

	// Hooks
	const { activeTab, setActiveTab, getTabLabel, getTabCacheKey } = useIndex({
		tabEnum: ClaimsTabs,
		defaultTab: doesUserHaveRoles(user, [UserRole.ClaimsAnalyst])
			? ClaimsTabs.AnalystDiary
			: doesUserHaveRoles(user, [UserRole.ClaimsViewOnly, UserRole.ClaimsNetworkManager])
				? ClaimsTabs.All
				: ClaimsTabs.Assigned,
		tabCacheKeys: [assignedCacheKey, allCacheKey, analystDiaryCacheKey, managerDiaryCacheKey, adminDiaryCacheKey]
	});
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const toast = useClaimsToast();

	/**
	 * Define columns to show in the DataTable
	 */
	const columns: Array<DataTableColumn> = [
		{
			header: "Id",
			id: "id",
			filterable: true,
			minWidth: "5ch",
			isHidden: false,
			isSelectable: true,
			linkable: true,
			isSortable: true,
			allData: true,
			valueMap: (claim: KVP) => <LinkableTableCell To={`/claim/${claim["id"]}/detail`} Contents={claim["id"]} />
		},
		{
			header: "PASS Case ID",
			id: "caseId",
			filterable: true,
			isHidden: false,
			isSortable: true,
			minWidth: "5ch",
			linkable: true,
			allData: true,
			valueMap: (claim: KVP) => {
				if (!claim["caseId"]) return <Box>N/A</Box>;
				const passCaseLink = createExternalLinkFromEnv(EnvKeys.PassUrl, claim["caseId"]?.toString());
				return <LinkableTableCell To={passCaseLink} Contents={claim["caseId"]} />;
			}
		},
		{
			header: "LAMP Case ID",
			id: "lampCaseId",
			filterable: true,
			minWidth: "5ch",
			isHidden: false,
			isSortable: true,
			linkable: true,
			allData: true,
			valueMap: (claim: KVP) => {
				if (!claim["lampCaseId"]) return <Box>N/A</Box>;
				const lampCaseLink = createExternalLinkFromEnv(EnvKeys.LampCaseUrl, claim["lampCaseId"]?.toString());
				return <LinkableTableCell To={lampCaseLink} Contents={claim["lampCaseId"]} />;
			}
		},
		{
			header: "Claims Analyst",
			id: "assignedAnalystName",
			filterable: true,
			minWidth: "14ch",
			isHidden:
				[ClaimsTabs.AdminDiary, ClaimsTabs.AnalystDiary, ClaimsTabs.ManagerDiary, ClaimsTabs.Assigned].indexOf(
					activeTab
				) > -1,
			isSortable: true
		},
		{
			header: "Claims Analyst ID",
			id: "assignedAnalystId",
			filterable: false,
			isHidden: true,
			isSortable: false
		},
		{
			header: "Legal Jurisdiction",
			id: "legalMatterState",
			filterable: true,
			minWidth: "14ch",
			isHidden: false,
			isSortable: true
		},
		{
			header: "Attorney ID",
			id: "attyId",
			filterable: true,
			minWidth: "5ch",
			isHidden: false,
			isSortable: true,
			linkable: true,
			allData: true,
			valueMap: (claim: KVP) => {
				const lampAttorneyLink = createExternalLinkFromEnv(EnvKeys.LampAttorneyUrl, claim["attyId"]?.toString());
				return (
					<Flex align="center" gap="1">
						<LinkableTableCell To={lampAttorneyLink} Contents={claim["attyId"]} />
						<AttorneyActiveBadge active={claim["attyActive"] === 1} />
					</Flex>
				);
			}
		},
		{
			header: "Attorney Name",
			id: "attyName",
			filterable: true,
			minWidth: "14ch",
			isHidden: false,
			isSortable: true
		},
		{
			header: "Member ID",
			id: "memberId",
			filterable: true,
			minWidth: "5ch",
			isHidden: false,
			isSortable: true
		},
		{
			header: "Member Name",
			id: "memberName",
			filterable: true,
			minWidth: "20ch",
			isHidden: false,
			isSortable: true
		},
		{
			header: "Status",
			id: "status",
			filterable: true,
			minWidth: "18ch",
			isHidden: false,
			isSortable: true,
			allData: true,
			valueMap: (claim: KVP) => (
				<HStack align="flex-start" justify="space-between" spacing={2} w="full">
					<Box flex="1" minW={0}>
						{claim["status"]}
					</Box>
					{claim["isHeldForAudit"] === 1 && activeTab !== ClaimsTabs.ManagerDiary && (
						<Badge colorScheme="purple" minW="56px" textAlign="center" variant="subtle">
							Audit
						</Badge>
					)}
				</HStack>
			),
			customFilter: {
				type: CustomColumnFilterType.Select,
				options: Object.entries(ClaimsStatus)
					.flatMap(([key, value]) => ({
						label: getClaimStatusFromBffStatus(value as ClaimsStatus),
						value: key.replaceAll("_", "").toLowerCase()
					}))
					.sort((a, b) => (a.label < b.label ? -1 : 1))
			}
		},
		{
			header: "Audit Hold",
			id: "isHeldForAudit",
			filterable: true,
			minWidth: "10ch",
			isHidden: activeTab !== ClaimsTabs.ManagerDiary,
			isSortable: false,
			valueMap: (value: string | number) => (value === 1 ? "Held" : "Not Held"),
			customFilter: {
				type: CustomColumnFilterType.Select,
				options: [
					{ label: "Held", value: "true" },
					{ label: "Not Held", value: "false" }
				]
			}
		},
		{
			header: "Created By",
			id: "createdBy",
			filterable: true,
			minWidth: "15ch",
			isHidden: false,
			isSortable: true
		},
		{
			header: "Created By User ID",
			id: "createdByUserId",
			filterable: false,
			isHidden: true,
			isSortable: false
		},
		{
			header: "Received",
			id: "receivedOn",
			filterable: true,
			minWidth: "10ch",
			isHidden: false,
			isSortable: true,
			isDate: true,
			customFilter: {
				type: CustomColumnFilterType.DatePicker
			}
		},
		{
			header: "Last Updated",
			id: "updatedAt",
			filterable: false,
			minWidth: "10ch",
			isHidden: false,
			isSortable: true,
			isDate: true
		},
		{
			header: "Due",
			id: "dueDate",
			filterable: false,
			minWidth: "10ch",
			isHidden: false,
			isSortable: true,
			isDate: true
		},
		{
			header: "",
			id: "actions",
			filterable: false,
			minWidth: "10ch",
			isHidden: false,
			isSelectable: false,
			linkable: false,
			isSortable: false,
			allData: true,
			valueMap: (claim: KVP) =>
				claim["status"] === ClaimStatus.Draft ? (
					<Button
						as={Link}
						to={generatePath(RouterPathMapping.ClaimIntake, { id: claim["id"]?.toString() })}
						isDisabled={!hasGeneralAdminAccess}
						pointerEvents={!hasGeneralAdminAccess ? "none" : "auto"}
						marginRight={4}
						size="sm"
						minWidth="74px"
						colorScheme="orange"
						padding={2}>
						Continue
					</Button>
				) : (
					<Button
						as={Link}
						to={generatePath(RouterPathMapping.ClaimDetail, { id: claim["id"]?.toString() })}
						marginRight={4}
						size="sm"
						minWidth="74px"
						colorScheme="orange"
						padding={2}>
						View
					</Button>
				)
		}
	];

	/**
	 * Handles switching between active tabs
	 * Note: Each tab has its own URL to allow for deep linking
	 * @param index The index of the chosen tab
	 */
	const handleChangedTab = (index: number): void => {
		setActiveTab(index);
		navigate(`/claims/${getTabLabel(index)}?${getTabCacheKey(index)}`);
	};

	const setCacheKeyForActiveTab = (cacheKey: string): void => {
		switch (activeTab) {
			case 0:
				if (cacheKey !== assignedCacheKey) setAssignedCacheKey(cacheKey);
				break;
			case 1:
				if (cacheKey !== allCacheKey) setAllCacheKey(cacheKey);
				break;
			case 2:
				if (cacheKey !== analystDiaryCacheKey) setAnalystDiaryCacheKey(cacheKey);
				break;
			case 3:
				if (cacheKey !== managerDiaryCacheKey) setManagerDiaryCacheKey(cacheKey);
				break;
			case 4:
				if (cacheKey !== adminDiaryCacheKey) setAdminDiaryCacheKey(cacheKey);
				break;
			default:
				console.warn(`Attempting to set cacheKey for unknown index => [${activeTab}]`);
		}
	};

	const getAssignedAnalystForActiveTab = (): number | undefined => {
		switch (activeTab) {
			case 0:
				return user?.id;
			case 2:
			case 3:
			case 1:
			case 4:
				return;
			default:
				console.warn(`Trying to get Assigned Analyst for unknown activeTab => ${activeTab}`);
				return;
		}
	};

	/**
	 * Helper function to allow for child objects to trigger a fetch of Claims data
	 * with the active tab's cacheKey
	 * @param cacheKey The cacheKey of the currently active table
	 */
	const handleFetchClaimsData = (cacheKey: string, diaryRole?: string): void => {
		if (!isEmpty(cacheKey)) {
			setIsLoading(true);
			setCacheKeyForActiveTab(cacheKey);

			const assignedAnalystId = getAssignedAnalystForActiveTab();

			getClaimsList(getCacheKeyObject(cacheKey), assignedAnalystId, undefined, diaryRole)
				.then((response: ClaimsListResponse) => {
					// Build response data
					const returnedClaimsData: Array<KVP> = response.getClaimList().map((claim: ApiClaim) => {
						const atty: ClaimPerson | undefined = claim.getAtty();
						const member: ClaimPerson | undefined = claim.getMember();
						const createdByPerson: ClaimPerson | undefined = claim.getCreatedByUser();
						const assignedAnalyst: ClaimPerson | undefined = claim.getAssignedAnalyst();
						if (atty) {
							const newClaim: KVP = {
								id: claim.getId(),
								caseId: claim.getCaseId(),
								lampCaseId: claim.getLampCaseId(),
								attyId: atty.getId(),
								attyName: atty.getName(),
								attyActive: claim.getAttyActive() ? 1 : 0,
								assignedAnalystId: assignedAnalyst?.getId() ?? "N/A",
								assignedAnalystName: assignedAnalyst?.getName() ?? "",
								memberId: member?.getId() ?? "",
								memberName: member?.getName() ?? "",
								createdBy: createdByPerson?.getName() ?? "",
								createdByUserId: createdByPerson?.getId() ?? "N/A",
								legalMatterState: claim.getLegalMatterState()?.getCode() ?? "",
								receivedOn: claim.getReceivedOn(),
								createdAt: convertUnixTimestampToLocalDate(claim.getCreatedAt()).toLocaleString(),
								updatedAt: convertUnixTimestampToLocalDate(claim.getUpdatedAt()).toLocaleString(),
								dueDate: claim.getDueDate() ? convertProtoDateToDateTime(claim.getDueDate()).toLocaleString() : "",
								status: getClaimStatusFromBffStatus(claim.getStatus()),
								isHeldForAudit: claim.hasIsHeldForAudit() && claim.getIsHeldForAudit() ? 1 : 0
							};

							return newClaim;
						} else {
							console.error(
								"Encountered a problem wherein a call to fetch the Claim was emitted but the Atty/Member was not found!"
							);
							toast("Error", "There was a problem fetching the Claim", "error");
							throw Error("attempted to process a claim with missing atty/member");
						}
					});
					const foundPaginationData: ApiPaginationResponse | undefined = response.getPaginationResponse();
					if (foundPaginationData) {
						const returnedPaginationData: PaginationResponse = {
							current_page: foundPaginationData.getCurrentPage(),
							first_page: foundPaginationData.getFirstPage(),
							last_page: foundPaginationData.getLastPage(),
							perPage: foundPaginationData.getLimit(),
							total: foundPaginationData.getTotal()
						};

						if (activeTab === ClaimsTabs.AnalystDiary) {
							setAnalystDiaryClaimsData({
								data: returnedClaimsData,
								meta: returnedPaginationData
							});
						} else if (activeTab === ClaimsTabs.ManagerDiary) {
							setManagerDiaryClaimsData({
								data: returnedClaimsData,
								meta: returnedPaginationData
							});
						} else if (activeTab === ClaimsTabs.AdminDiary) {
							setAdminDiaryClaimsData({
								data: returnedClaimsData,
								meta: returnedPaginationData
							});
						} else if (activeTab === ClaimsTabs.Assigned) {
							setAssignedClaimsData({
								data: returnedClaimsData,
								meta: returnedPaginationData
							});
						} else if (activeTab === ClaimsTabs.All) {
							setAllClaimsData({
								data: returnedClaimsData,
								meta: returnedPaginationData
							});
						}
					} else {
						toast("Error", "There was a problem deciphering the pagination metadata", "error");
						console.error("Attempting to parse pagination metadata that does not exist!!");
					}
				})
				.catch((err) => {
					toast("Error", (err as RpcError).message, "error");
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	const toggleLoading = (isPageLoading: boolean): void => setIsLoading(isPageLoading);

	const getActiveTabIndex = (index: number): number => {
		// Due to UI problems, we need to finesse the index
		if (user) {
			const userRoleList: Array<string> = user.role.split(",");
			const hasAnalystRole: boolean = userRoleList.indexOf(UserRole.ClaimsAnalyst) > -1;
			const hasManagerRole: boolean = userRoleList.indexOf(UserRole.ClaimsManager) > -1;
			const hasAdminRole: boolean = userRoleList.indexOf(UserRole.ClaimsAdmin) > -1;

			// Unfortunately we need to run down permutations
			if (hasAnalystRole && hasManagerRole && hasAdminRole) {
				switch (index) {
					case ClaimsTabs.AnalystDiary:
						return 0;
					case ClaimsTabs.Assigned:
						return 1;
					case ClaimsTabs.All:
						return 2;
					case ClaimsTabs.ManagerDiary:
						return 3;
					case ClaimsTabs.AdminDiary:
						return 4;
					default:
						console.warn(`Trying to finesse an unknown index => [${index}]`);
						return 0;
				}
			} else if (hasAnalystRole && hasManagerRole) {
				switch (index) {
					case ClaimsTabs.AnalystDiary:
						return 0;
					case ClaimsTabs.Assigned:
						return 1;
					case ClaimsTabs.All:
						return 2;
					case ClaimsTabs.ManagerDiary:
						return 3;
					default:
						console.warn(`Trying to finesse an unknown index => [${index}]`);
						return 0;
				}
			} else if (hasAnalystRole && hasAdminRole) {
				switch (index) {
					case ClaimsTabs.AnalystDiary:
						return 0;
					case ClaimsTabs.Assigned:
						return 1;
					case ClaimsTabs.All:
						return 2;
					case ClaimsTabs.AdminDiary:
						return 3;
					default:
						console.warn(`Trying to finesse an unknown index => [${index}]`);
						return 0;
				}
			} else if (hasAdminRole && hasManagerRole) {
				switch (index) {
					case ClaimsTabs.Assigned:
						return 0;
					case ClaimsTabs.All:
						return 1;
					case ClaimsTabs.ManagerDiary:
						return 2;
					case ClaimsTabs.AdminDiary:
						return 3;
					default:
						console.warn(`Trying to finesse an unknown index => [${index}]`);
						return 0;
				}
			} else if (hasAnalystRole) {
				switch (index) {
					case ClaimsTabs.AnalystDiary:
						return 0;
					case ClaimsTabs.Assigned:
						return 1;
					case ClaimsTabs.All:
						return 2;
					default:
						console.warn(`Trying to finesse an unknown index => [${index}]`);
						return 0;
				}
			} else if (hasManagerRole) {
				switch (index) {
					case ClaimsTabs.Assigned:
						return 0;
					case ClaimsTabs.All:
						return 1;
					case ClaimsTabs.ManagerDiary:
						return 2;
					default:
						console.warn(`Trying to finesse an unknown index => [${index}]`);
						return 0;
				}
			} else if (hasAdminRole) {
				switch (index) {
					case ClaimsTabs.Assigned:
						return 0;
					case ClaimsTabs.All:
						return 1;
					case ClaimsTabs.AdminDiary:
						return 2;
					default:
						console.warn(`Trying to finesse an unknown index => [${index}]`);
						return 0;
				}
			} else if (hasGeneralClaimsAccess) {
				switch (index) {
					case ClaimsTabs.All:
						return 1;
					default:
						console.warn(`Trying to finesse an unknown index => [${index}]`);
						return 0;
				}
			} else return -1;
		} else return -1;
	};

	return (
		<>
			<LayoutWrapper>
				<Box data-testid="claims-wrapper">
					<Tabs
						colorScheme="orange"
						variant="enclosed-colored"
						margin="20px 10px"
						isLazy={true}
						index={getActiveTabIndex(activeTab)}>
						<Box display="flex" borderColor="transparent" marginBottom="1px" marginLeft="1px">
							{doesUserHaveRoles(user, [UserRole.ClaimsAnalyst, UserRole.ClaimsManager, UserRole.ClaimsAdmin]) && (
								<TabList marginLeft="15px" position="relative">
									{doesUserHaveRoles(user, [UserRole.ClaimsAnalyst]) && (
										<Tab
											borderTopWidth="0px"
											backgroundColor="gray.200"
											onClick={() => handleChangedTab(ClaimsTabs.AnalystDiary)}
											fontWeight="bold"
											fontSize="19px"
											marginRight="4px !important"
											_selected={{ bg: "white" }}>
											Analyst Diary
										</Tab>
									)}
									<Tab
										borderTopWidth="0px"
										backgroundColor="gray.200"
										onClick={() => handleChangedTab(ClaimsTabs.Assigned)}
										fontWeight="bold"
										fontSize="19px"
										marginRight="4px !important"
										_selected={{ bg: "white" }}>
										My Claims
									</Tab>
									<Tab
										borderTopWidth="0px"
										backgroundColor="gray.200"
										onClick={() => handleChangedTab(ClaimsTabs.All)}
										fontWeight="bold"
										fontSize="19px"
										_selected={{ bg: "white" }}>
										All Claims
									</Tab>
									{doesUserHaveRoles(user, [UserRole.ClaimsManager]) && (
										<Tab
											borderTopWidth="0px"
											backgroundColor="gray.200"
											onClick={() => handleChangedTab(ClaimsTabs.ManagerDiary)}
											fontWeight="bold"
											fontSize="19px"
											marginLeft="4px !important"
											_selected={{ bg: "white" }}>
											Manager Diary
										</Tab>
									)}
									{doesUserHaveRoles(user, [UserRole.ClaimsAdmin]) && (
										<Tab
											borderTopWidth="0px"
											backgroundColor="gray.200"
											onClick={() => handleChangedTab(ClaimsTabs.AdminDiary)}
											fontWeight="bold"
											fontSize="19px"
											marginLeft="4px !important"
											_selected={{ bg: "white" }}>
											Admin Diary
										</Tab>
									)}
									{/* Filler background tab */}
									<Tab
										position="absolute"
										backgroundColor="#e9e9e9"
										zIndex="-1"
										left="0"
										right="0"
										bottom="0"
										top="0"
										boxShadow="1px 2px 9px 1px gainsboro"></Tab>
								</TabList>
							)}
						</Box>
						<TabPanels>
							{doesUserHaveRoles(user, [UserRole.ClaimsAnalyst]) && (
								<TabPanel pt="0">
									{activeTab === ClaimsTabs.AnalystDiary && (
										<ClaimsIndexTable
											claimsData={analystDiaryClaimsData}
											columns={columns}
											searchParams={searchParams}
											handleFetchClaims={(cacheKey) =>
												handleFetchClaimsData(cacheKey, UserRole.ClaimsAnalyst.toString())
											}
											handleManualRefetchTrigger={() =>
												handleFetchClaimsData(analystDiaryCacheKey, UserRole.ClaimsAnalyst.toString())
											}
											handleToggleLoading={toggleLoading}
											defaultParams={analystDiaryDefaultParams}
											assignedAnalystId={user?.id}
											cacheKey={analystDiaryCacheKey}
											isLoading={isLoading}
										/>
									)}
								</TabPanel>
							)}
							<TabPanel pt="0">
								{activeTab === ClaimsTabs.Assigned && (
									<ClaimsIndexTable
										claimsData={assignedClaimsData}
										columns={columns}
										searchParams={searchParams}
										handleFetchClaims={handleFetchClaimsData}
										handleManualRefetchTrigger={() => handleFetchClaimsData(assignedCacheKey)}
										handleToggleLoading={toggleLoading}
										defaultParams={assignedClaimsDefaultParams}
										assignedAnalystId={user?.id}
										cacheKey={assignedCacheKey}
										isLoading={isLoading}
									/>
								)}
							</TabPanel>
							<TabPanel pt="0">
								{activeTab === ClaimsTabs.All && (
									<ClaimsIndexTable
										claimsData={allClaimsData}
										columns={columns}
										searchParams={searchParams}
										handleFetchClaims={handleFetchClaimsData}
										handleManualRefetchTrigger={() => handleFetchClaimsData(allCacheKey)}
										handleToggleLoading={toggleLoading}
										defaultParams={allClaimsDefaultParams}
										cacheKey={allCacheKey}
										isLoading={isLoading}
										showAdvancedFilters={true}
									/>
								)}
							</TabPanel>
							{doesUserHaveRoles(user, [UserRole.ClaimsManager]) && (
								<TabPanel pt="0">
									{activeTab === ClaimsTabs.ManagerDiary && (
										<ClaimsIndexTable
											claimsData={managerDiaryClaimsData}
											columns={columns}
											searchParams={searchParams}
											handleFetchClaims={(cacheKey) =>
												handleFetchClaimsData(cacheKey, UserRole.ClaimsManager.toString())
											}
											handleManualRefetchTrigger={() =>
												handleFetchClaimsData(managerDiaryCacheKey, UserRole.ClaimsManager.toString())
											}
											handleToggleLoading={toggleLoading}
											defaultParams={managerDiaryDefaultParams}
											assignedAnalystId={user?.id}
											cacheKey={managerDiaryCacheKey}
											isLoading={isLoading}
										/>
									)}
								</TabPanel>
							)}
							{doesUserHaveRoles(user, [UserRole.ClaimsAdmin]) && (
								<TabPanel pt="0">
									{activeTab === ClaimsTabs.AdminDiary && (
										<ClaimsIndexTable
											claimsData={adminDiaryClaimsData}
											columns={columns}
											searchParams={searchParams}
											handleFetchClaims={(cacheKey) => handleFetchClaimsData(cacheKey, UserRole.ClaimsAdmin.toString())}
											handleManualRefetchTrigger={() =>
												handleFetchClaimsData(adminDiaryCacheKey, UserRole.ClaimsAdmin.toString())
											}
											handleToggleLoading={toggleLoading}
											defaultParams={adminDiaryDefaultParams}
											assignedAnalystId={user?.id}
											cacheKey={adminDiaryCacheKey}
											isLoading={isLoading}
										/>
									)}
								</TabPanel>
							)}
						</TabPanels>
					</Tabs>
				</Box>
			</LayoutWrapper>
		</>
	);
};

export default ClaimsPage;
